import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, ChevronRight } from 'lucide-react'
import type { SiteInfo } from '@/types'

interface SiteSelectorProps {
  sites: SiteInfo[]
  selectedSiteId: string | null
  onSelectSite: (siteId: string | null) => void
}

export default function SiteSelector({
  sites,
  selectedSiteId,
  onSelectSite,
}: SiteSelectorProps) {
  const { t } = useTranslation()
  const [showSimOnly, setShowSimOnly] = useState(false)

  const isSimSite = (id: string) =>
    id === 'web_simulator' || id.startsWith('sim-')
  const hasSimSite = sites.some((s) => isSimSite(s.id))
  const filteredSites = showSimOnly
    ? sites.filter((s) => isSimSite(s.id))
    : sites.filter((s) => !isSimSite(s.id))

  return (
    <div className="w-56 flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <MapPin className="w-3.5 h-3.5" />
          {t('sites.site')}
        </div>
        {hasSimSite && (
          <button
            onClick={() => setShowSimOnly((v) => !v)}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
              showSimOnly
                ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
            }`}
          >
            {showSimOnly ? 'SIM 表示中' : 'SIM 非表示'}
          </button>
        )}
      </div>
      <ul className="overflow-y-auto flex-1">
        {filteredSites.map((site) => (
          <li key={site.id}>
            <button
              onClick={() => onSelectSite(site.id)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                selectedSiteId === site.id
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="truncate flex items-center gap-1.5">
                {site.name}
                {isSimSite(site.id) && (
                  <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 flex-shrink-0">
                    SIM
                  </span>
                )}
              </span>
              <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
